import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { STORAGE_KEYS } from '../constants/storage-keys';

export type LangCode = 'id' | 'en' | 'vi';

export interface LangOption {
  code: LangCode;
  /** Kode pendek untuk tombol di topbar. */
  short: string;
  /** Endonim — nama bahasa dalam bahasa itu sendiri. */
  label: string;
}

/**
 * Bahasa yang AKTIF di aplikasi.
 *
 * Untuk sekarang hanya Indonesia & English (keputusan manajemen). Terjemahan
 * Vietnam (`vi.json`) sengaja TIDAK dihapus — tinggal aktifkan kembali dengan
 * menambahkan barisnya di sini bila sudah siap; bendera & tipe `LangCode` juga
 * masih mendukung 'vi'.
 */
export const LANGUAGES: LangOption[] = [
  { code: 'id', short: 'ID', label: 'Bahasa Indonesia' },
  { code: 'en', short: 'EN', label: 'English' },
  // { code: 'vi', short: 'VI', label: 'Tiếng Việt' },
];

export const DEFAULT_LANG: LangCode = 'id';

/**
 * Bahasa aktif aplikasi — mengikuti pola `ThemeService`: signal publik readonly,
 * nilai awal dibaca dari localStorage, urutan `set signal → apply → persist`.
 *
 * Default selalu Indonesia (tanpa auto-detect bahasa browser), sesuai keputusan
 * produk: pengguna berpindah bahasa secara manual lewat switcher di topbar.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);

  readonly languages = LANGUAGES;
  readonly lang = signal<LangCode>(this.readInitialLang());

  constructor() {
    this.applyLang(this.lang());
  }

  setLang(lang: LangCode): void {
    if (!LANGUAGES.some((l) => l.code === lang)) return;
    this.lang.set(lang);
    this.applyLang(lang);
    try {
      localStorage.setItem(STORAGE_KEYS.language, lang);
    } catch (err) {
      // Mode privasi bisa memblokir localStorage; bahasa tetap aktif untuk sesi ini.
      console.warn('LANGUAGE: gagal menyimpan preferensi bahasa:', err);
    }
  }

  /** Opsi bahasa yang sedang aktif (untuk label tombol switcher). */
  current(): LangOption {
    return LANGUAGES.find((l) => l.code === this.lang()) ?? LANGUAGES[0];
  }

  private applyLang(lang: LangCode): void {
    this.transloco.setActiveLang(lang);
    // Bantu screen reader & aturan CSS `:lang()` mengenali bahasa halaman.
    document.documentElement.setAttribute('lang', lang);
  }

  private readInitialLang(): LangCode {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.language) as LangCode | null;
      if (stored && LANGUAGES.some((l) => l.code === stored)) return stored;
    } catch (err) {
      console.warn('LANGUAGE: gagal membaca preferensi bahasa:', err);
    }
    return DEFAULT_LANG;
  }
}
